import { useState, useEffect } from "react";
import { RgbaStringColorPicker } from "react-colorful";

interface RgbPickerProps {
  color: string | null;
  onChange: (color: string) => void;
  onSaveCustomColor?: (color: string) => void;
}

const rgbaToHex = (rgba: string): string => {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (!match) return rgba;

  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);

  const toHex = (n: number) => {
    const hex = n.toString(16).padStart(2, "0");
    return hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const hexToRgba = (hex: string): string => {
  if (hex === "transparent") {
    return "rgba(255, 255, 255, 0)";
  }

  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, 1)`;
};

export const RgbPicker = ({
  color,
  onChange,
  onSaveCustomColor,
}: RgbPickerProps) => {
  const [rgbaColor, setRgbaColor] = useState(() => {
    if (!color || color === "transparent") {
      return "rgba(255, 255, 255, 1)";
    }
    return color.startsWith("#") ? hexToRgba(color) : color;
  });

  useEffect(() => {
    if (color && color !== "transparent") {
      const newRgba = color.startsWith("#") ? hexToRgba(color) : color;
      setRgbaColor(newRgba);
    }
  }, [color]);

  const handleChange = (newColor: string) => {
    setRgbaColor(newColor);
    const hexColor = rgbaToHex(newColor);
    onChange(hexColor);
  };

  const handleSave = () => {
    const hexColor = rgbaToHex(rgbaColor);
    if (onSaveCustomColor) {
      onSaveCustomColor(hexColor);
    }
  };

  return (
    <div className="color-picker__rgb-picker">
      <RgbaStringColorPicker color={rgbaColor} onChange={handleChange} />
      {onSaveCustomColor && (
        <button
          type="button"
          className="color-picker__save-custom-button"
          onClick={handleSave}
          title="Save custom color"
        >
          Save Color
        </button>
      )}
    </div>
  );
};
